import { Inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { merge as mergeDeep } from 'lodash-es';
import { asyncScheduler } from 'rxjs';
import { DeepPartial } from 'utility-types';

import { ApolloCacheFactory, ApolloCacheManager } from '@shure/cloud/shared/apollo';
import { CloudFeatureFlagsService, FEATURE_FLAG_SERVICE } from '@shure/cloud/shared/utils/feature-flags';

import {
	InventoryDeviceByIdQueryDocument,
	InventoryDeviceByIdQueryOpResult,
	InventoryDeviceByIdQueryVariables,
	InventoryDeviceFragment
} from './graphql/generated/cloud-sys-api';

@Injectable({ providedIn: 'root' })
export class SysApiDeviceInventoryApolloCache {
	private readonly groupFeatureEnabled = toSignal(this.featureFlagsService.getFeatureFlag$('cdm-device-groups'), {
		initialValue: false
	});

	constructor(
		private readonly apolloCacheManager: ApolloCacheManager,
		private readonly apolloCache: ApolloCacheFactory,
		@Inject(FEATURE_FLAG_SERVICE) private readonly featureFlagsService: CloudFeatureFlagsService
	) {}

	public seedEntry(device: InventoryDeviceFragment): void {
		const variables: InventoryDeviceByIdQueryVariables = {
			nodeId: device.id,
			requestGroup: this.groupFeatureEnabled()
		};
		this.apolloCache.cache?.writeQuery({
			query: InventoryDeviceByIdQueryDocument,
			data: { node: device },
			variables,
			broadcast: false, // significant perf cost if this is TRUE.
			overwrite: true
		});
	}

	public removeEntry(id: string): void {
		// Schedule the cache removal for a short amount of time in the future
		// This allows any watchQueries to get removed w/out reporting errors.
		asyncScheduler.schedule(
			() => this.apolloCacheManager.getApolloCache('sysApi')?.evict({ id: `Device:${id}`, broadcast: false }),
			1000
		);
	}

	public readDeviceFragment(id: string): InventoryDeviceFragment | undefined {
		const queryVars: InventoryDeviceByIdQueryVariables = {
			nodeId: id,
			requestGroup: this.groupFeatureEnabled()
		};

		const deviceData = this.apolloCache.cache?.readQuery<
			InventoryDeviceByIdQueryOpResult,
			InventoryDeviceByIdQueryVariables
		>({
			query: InventoryDeviceByIdQueryDocument,
			variables: queryVars
		});

		const deviceFragment = deviceData?.node;
		if (!deviceFragment || !('isDevice' in deviceFragment)) {
			return undefined;
		}
		return deviceFragment as InventoryDeviceFragment;
	}

	public updateFeature<TFeatureKey extends keyof InventoryDeviceFragment['features']>(
		id: string,
		featureKey: TFeatureKey,
		newData: DeepPartial<InventoryDeviceFragment['features'][TFeatureKey]>
	): void {
		const queryVars: InventoryDeviceByIdQueryVariables = {
			nodeId: id,
			requestGroup: this.groupFeatureEnabled()
		};

		const cachedData = this.apolloCache.cache?.readQuery<
			InventoryDeviceByIdQueryOpResult,
			InventoryDeviceByIdQueryVariables
		>({
			query: InventoryDeviceByIdQueryDocument,
			variables: queryVars
		});

		const cachedDevice = cachedData?.node;
		if (!cachedDevice || !('isDevice' in cachedDevice)) {
			return;
		}

		this.apolloCache.cache?.writeQuery<InventoryDeviceByIdQueryOpResult, InventoryDeviceByIdQueryVariables>({
			query: InventoryDeviceByIdQueryDocument,
			variables: queryVars,
			data: {
				node: mergeDeep({}, cachedDevice, {
					features: {
						// eslint-disable-next-line @typescript-eslint/naming-convention
						__typename: 'DeviceFeatures',
						[featureKey]: newData
					}
				})
			}
		});
	}
}
